import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useFloatingBox from '@aurora/shared-client/components/context/FloatingBoxContext/useFloatingBox';
import { FloatingBoxLocation } from '@aurora/shared-client/components/context/FloatingBoxContext/FloatingBoxContextProvider';
import useSurvey from '@aurora/shared-client/components/surveys/UseSurvey/useSurvey';
import { LocalStorageKeys } from '@aurora/shared-types/community/enums';
import { NodeType } from '@aurora/shared-types/nodes/enums';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { getLog } from '@aurora/shared-utils/log';
import dynamic from 'next/dynamic';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SURVEY_PROMPT_FLOATING_BOX } from '../SurveyPromptFloatingBox/SurveyPromptFloatingBox';
import type {
  ChildNode,
  CookieProperties,
  CoreNodeEdge
} from '@aurora/shared-generated/types/graphql-schema-types';
import {
  surveyCheck,
  updateSurveyDeclinedCookie,
  updateSurveyVisitCookie
} from '@aurora/shared-client/helpers/surveys/SurveyCheckHelper';
import type { ValueSurveyBehaviorQuery } from '../../../types/graphql-types';

const SurveyPromptFloatingBox = dynamic(
  () => import('../SurveyPromptFloatingBox/SurveyPromptFloatingBox'),
  {
    ssr: false
  }
);

const SurveyModal = dynamic(() => import('../SurveyModal/SurveyModal'), {
  ssr: false
});

const log = getLog(module);

declare global {
  interface Window {
    /**
     * Flag to check if the survey launcher has been run once
     */
    auroraSurveyLauncherRunOnce?: boolean;
    /**
     * Tracking the context node so that we can re-run the survey launcher when the user changes nodes.
     * Attempts were made to track this using events, but it was not reliable.
     */
    auroraSurveyCurrentNodeId?: string;
    /**
     * Tracking the auth user so that we can re-run the survey launcher when the user logs in or out.
     * Attempts were made to track this using events, but it was not reliable.
     */
    auroraSurveyAuthUserId?: string;
  }
}

interface SurveyLauncherContentProps {
  cookieProperties: CookieProperties;
  surveyBehaviorData: ValueSurveyBehaviorQuery;
  functionalCookiesAccepted: boolean;
}

/**
 * Survey Launcher Content component, handles the logic for when to launch the survey
 * Assumes all data is loaded
 * @author Nicolas Pascual
 */
const SurveyLauncherContent: React.FC<SurveyLauncherContentProps> = ({
  cookieProperties,
  surveyBehaviorData,
  functionalCookiesAccepted
}) => {
  const { floatingBox, addBox, showBox, removeBox } = useFloatingBox(SURVEY_PROMPT_FLOATING_BOX, {
    location: FloatingBoxLocation.BOTTOM_RIGHT
  });
  const { contextNode, authUser } = useContext(AppContext);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

  const [showModal, setShowModal] = useState(false);
  const { ready, computedSurvey } = useSurvey(!showModal);

  const { isCookieBannerEnabled } = cookieProperties;

  /**
   * Validates that the user is currently in the required node for the survey or in a descendant of that node.
   * @param requiredNodeId
   */
  const isOnRequiredNode = useCallback(
    (requiredNodeId: string): boolean => {
      // current node is Community
      if (contextNode.nodeType === NodeType.COMMUNITY) {
        return contextNode.id === requiredNodeId;
      }

      // current node is the required one
      if (contextNode.id === requiredNodeId) {
        return true;
      }

      // current node is a descendant of the required one
      const { ancestors } = contextNode as unknown as ChildNode;
      if (ancestors) {
        const nodeEdges = [...ancestors.edges];
        return nodeEdges.some((edge: CoreNodeEdge) => edge.node.id === requiredNodeId);
      }

      return false;
    },
    [contextNode]
  );

  /**
   * Starts a timer to show the survey prompt, if the user is in the required node. The local storage value set here
   * is removed whe user either logs in or logs out.
   */
  const startTimer = useCallback(
    (
      frequencyMinutes: number,
      requiredNodeId: string | null,
      addBoxCallback: () => void
    ): ReturnType<typeof setTimeout> => {
      let remainingTime: number = 0;

      try {
        let visitStartTime = localStorage.getItem(LocalStorageKeys.VALUE_SURVEY_VISIT_START_TIME);
        if (!visitStartTime) {
          visitStartTime = Date.now().toString();
          localStorage.setItem(LocalStorageKeys.VALUE_SURVEY_VISIT_START_TIME, visitStartTime);
        }

        const frequencyInMilliseconds = frequencyMinutes * 60 * 1000;
        const elapsedTime = Date.now() - Number.parseInt(visitStartTime, 10);
        remainingTime = Math.max(frequencyInMilliseconds - elapsedTime, 0);
      } catch (error) {
        log.error('Error calculating remaining time, defaulting to 0.', error);
      }

      return setTimeout(() => {
        /**
         * Adds the survey prompt based on the 'required node' setting. This will be true when:
         *  1. There's no required node, meaning the survey can be shown anywhere.
         *  2. There's a required node and the user is currently in that node.
         */
        if (!requiredNodeId || isOnRequiredNode(requiredNodeId)) {
          addBoxCallback();
        }
      }, remainingTime);
    },
    [isOnRequiredNode]
  );

  useEffect(() => {
    async function doCheck() {
      // Only go into surveys logic if cookies banner is not enabled
      // Or if user has accepted cookies consent
      if (
        (!isCookieBannerEnabled || functionalCookiesAccepted) &&
        surveyBehaviorData &&
        checkPolicy(surveyBehaviorData.community.surveyProperties.valueSurveyEnabled) &&
        (window.auroraSurveyLauncherRunOnce !== true ||
          window.auroraSurveyCurrentNodeId !== contextNode.id ||
          window.auroraSurveyAuthUserId !== authUser.id)
      ) {
        window.auroraSurveyLauncherRunOnce = true;
        window.auroraSurveyCurrentNodeId = contextNode.id;
        window.auroraSurveyAuthUserId = authUser.id;

        // Wait for visit cookie to be set before checking if the survey can be shown
        await updateSurveyVisitCookie();

        if (checkPolicy(surveyBehaviorData.self.userPolicies.canTakeValueSurvey)) {
          // Calls checkCookies middleware to validate conditions are met for showing up the survey box
          const data = await surveyCheck();

          if (data?.validated && !showModal) {
            const requiredNodeId = data?.surveyNodeId;

            // special case for when the survey is first shown but then user navigates away from the node
            if (requiredNodeId && !isOnRequiredNode(requiredNodeId)) {
              removeBox();
            }

            // user can take the survey, start timer as frequencyMinutes will always be at least 1 minute
            timerRef.current = startTimer(data?.surveyFrequencyMinutes, requiredNodeId, addBox);
          }
          return data;
        } else {
          // special case for when the survey is first shown but then user auth changes
          removeBox();
        }
      }
    }

    doCheck();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [
    addBox,
    removeBox,
    startTimer,
    surveyBehaviorData,
    showModal,
    isOnRequiredNode,
    functionalCookiesAccepted,
    isCookieBannerEnabled,
    contextNode.id,
    authUser.id
  ]);

  function onReject() {
    // Calls setDeclinedSurveyCookie middleware to set completion cookie to declined
    updateSurveyDeclinedCookie();
    removeBox();
  }

  function onAccept() {
    removeBox();
    setShowModal(true);
  }

  function onClose() {
    setShowModal(false);
  }

  return (
    <>
      {showBox &&
        !showModal &&
        floatingBox(<SurveyPromptFloatingBox onReject={onReject} onAccept={onAccept} />)}
      {showModal && ready && <SurveyModal onClose={onClose} computedSurvey={computedSurvey} />}
    </>
  );
};

export default SurveyLauncherContent;
